// src/components/contacts/CreateContactDrawer.tsx
import React, { useEffect, useState } from 'react';
import { Drawer, Form, Input, Button, Space, message, Select } from 'antd';
import { createContact, type CreateContactRequest } from '../../services/contacts';
import { listSectors, type SectorDTO } from '../../services/sector';

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
};

const CreateContactDrawer: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const [form] = Form.useForm<CreateContactRequest>();
  const [loading, setLoading] = useState(false);
  const [sectors, setSectors] = useState<SectorDTO[]>([]);
  const [loadingSectors, setLoadingSectors] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setLoadingSectors(true);
        const list = await listSectors(true);
        setSectors(Array.isArray(list) ? list : []);
      } catch {
        setSectors([]);
      } finally {
        setLoadingSectors(false);
      }
    })();
  }, [open]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await createContact(values);
      message.success('Contatto creato con successo');
      form.resetFields();
      onCreated?.();
      onClose();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.message || 'Errore durante la creazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title="Nuovo Contatto"
      open={open}
      onClose={onClose}
      width={520}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose}>Annulla</Button>
          <Button type="primary" onClick={handleSubmit} loading={loading}>
            Salva
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item name="businessName" label="Azienda">
          <Input placeholder="Azienda S.r.l." />
        </Form.Item>

        <Space.Compact block style={{ gap: 12 }}>
          <Form.Item name="firstName" label="Nome" style={{ flex: 1 }}>
            <Input placeholder="Mario" />
          </Form.Item>
          <Form.Item name="lastName" label="Cognome" style={{ flex: 1 }}>
            <Input placeholder="Rossi" />
          </Form.Item>
        </Space.Compact>

        <Space.Compact block style={{ gap: 12 }}>
          <Form.Item
            name="email"
            label="Email"
            style={{ flex: 1 }}
            rules={[{ type: 'email', message: 'Email non valida' }]}
          >
            <Input placeholder="mario.rossi@azienda.it" />
          </Form.Item>
          <Form.Item name="phone" label="Telefono" style={{ flex: 1 }}>
            <Input placeholder="+39 333 1234567" />
          </Form.Item>
        </Space.Compact>

        <Form.Item name="website" label="Sito web">
          <Input placeholder="https://www.azienda.it" />
        </Form.Item>

        {/* Selettore Settore */}
        <Form.Item name="sectorId" label="Settore">
          <Select
            allowClear
            showSearch
            placeholder="Seleziona settore"
            loading={loadingSectors}
            optionFilterProp="label"
            options={sectors.map(s => ({ label: s.name, value: s.id }))}
          />
        </Form.Item>

        <Form.Item name="notes" label="Note">
          <Input.TextArea placeholder="Note interne..." autoSize={{ minRows: 3 }} />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default CreateContactDrawer;
